import type {
  ISvdCard,
  ISvdCardVariant,
  ISvdClassification,
  SvdNativeCardVariant,
  SvdNativeClassification,
  SvdNativeProduct,
} from '@src/types/svd-product-card.types'

/*
 * NOTE — no shape-guard (`isSvdProduct`) here on purpose.
 * The SVD rollout picks the BFF API version *before* the fetch (experiment on → v4 → the whole
 * response is SVD-shaped; off → v2/v3 → entirely legacy), so a single response is never mixed and
 * callers statically know the shape from the flag they already evaluated. A runtime SVD-vs-legacy
 * discriminator only becomes necessary if that premise breaks — e.g. a downstream/paginated
 * endpoint starts returning legacy-shaped cards inside a v4-served page. Reintroduce a
 * `productId`-typeof / `anchorAttributes`-presence guard here at that point.
 */

function mapClassification(classification: ISvdClassification | null): SvdNativeClassification | undefined {
  if (!classification) return undefined
  return {
    divisionId: classification.division_id,
    division: classification.division,
    departmentId: classification.department_id,
    department: classification.department,
    subdepartmentId: classification.subdepartment_id,
    subdepartment: classification.subdepartment,
    classId: classification.classId,
    className: classification.class,
  }
}

function mapCardVariant(variant: ISvdCardVariant): SvdNativeCardVariant {
  return {
    displayConfig: variant.displayConfig,
    anchorAttributes: variant.anchorAttributes,
    selectedAttributes: variant.selectedAttributes,
    variantId: variant.variantId,
    variantIds: variant.variantIds,
    svids: variant.svids,
    spid: variant.spid,
    title: variant.title,
    vendorType: variant.vendorType,
    url: variant.url,
    images: variant.images ?? [],
    price: variant.price,
    tags: variant.tags,
    promoConfig: variant.promoConfig,
    socialProofData: variant.socialProofData,
    atcDisabled: variant.atcDisabled,
    appExclusive: variant.appExclusive,
    orderableOnPlatforms: variant.orderableOnPlatforms,
  }
}

/**
 * Normalizes a raw SVD (v4 page-data) product card into the standalone native SVD product type.
 * Only normalizes wire-format looseness (string ids/ratings → numbers); does not alter semantics
 * — nullable `displayConfig` stays null for no-VDA products, no swatch-render translation. This
 * type is not consumed by the existing DS/legacy cards.
 *
 * Meant to be called per-product at the point the SVD card/section consumes it. If a future
 * consumer instead needs the whole `pageDataJson` tree normalized in place (SVD cards are nested
 * under `mobileContainerConfig`/`desktopContainerConfig` → `children` → `props.productCards`, with
 * recursive `subSections`), add a small recursive walker that finds every `productCards[]` array
 * and runs each entry through this function — rather than pushing tree traversal into the caller.
 */
export function mapSvdProduct(product: ISvdCard): SvdNativeProduct {
  return {
    productId: Number(product.productId) || 0,
    relatedIds: {
      contentfulId: product.relatedIds.contentfulId,
      spid: Number(product.relatedIds.spid) || 0,
    },
    title: product.title,
    slug: product.slug,
    type: product.type as 'Normal' | 'Bundle',
    thirdPartyDetails: {
      brand: product.thirdPartyDetails.brand ?? undefined,
      brandDisplayName: product.thirdPartyDetails.brandDisplayName ?? undefined,
    },
    reviews: {
      averageRating: Number(product.reviews.averageRating) || 0,
      ratingsTotal: product.reviews.ratingsTotal ?? 0,
      reviewCount: product.reviews.reviewCount,
    },
    totalCardVariants: product.totalCardVariants,
    cardVariants: product.cardVariants.map(mapCardVariant),
    flags: product.flags,
    classification: mapClassification(product.classification),
    customVariants: product.customVariants,
    colorVariants: product.colorVariants ?? [],
    socialProofData: product.socialProofData,
    isListable: product.isListable,
    gender: product.gender,
    productCategory: product.product_category,
    addOnProductOptionGroups: product.addOnProductOptionGroups,
    subscriptionEnabled: product.subscriptionEnabled,
    searchObjectId: product.responseContext?.searchObjectId ?? null,
  }
}
